/**
 * `/assets` — the Assets section landing.
 *
 * Three division cards over the hundred-machine population, each carrying its
 * machine count, the mines it is present at, how many machines sit in an
 * attention band, and a row per subclass that deep-links into the division
 * page already filtered.
 *
 * ## Source
 *
 * The demo reached the division pages from its left nav and from the generic
 * section hero `show('section','assets')` (demo line 2994 / `SECTINFO` line
 * 1994) — it had no dedicated landing. This scene is that hero, made explicit:
 * the same three destinations, with the counts derived rather than described.
 *
 * ## Data
 *
 * Eager only — `data/core.ts` plus `statusOf`. Nothing is fetched, so the page
 * has no loading state.
 *
 * @see src/versions/v1/modules/fleet/AssetsSectionScene.transform.ts
 * @see docs/logic/fleet/assets.md
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box, ChevronRight } from 'lucide-react';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { CONDITION_BAND_LABEL } from '../../schema/machine';
import { FLEET } from '../../data/core';
import './palette';
import { assetsBandTally, divisionCards } from './AssetsSectionScene.transform';

/** Demo line 1994 — the Assets section standfirst, extended with the division split. */
const STANDFIRST =
  'Every machine in the population, division by division. A division page carries the class ' +
  'figures, a render of the class with its flagged components called out, and every machine ' +
  'grouped by the mine it works at. The condition band on a card is the machine’s lowest ' +
  'component score.';

export function AssetsSectionScene() {
  const cards = useMemo(() => divisionCards(), []);
  const bands = useMemo(() => assetsBandTally(), []);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="AssetsSectionScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8" as="div">
          <SurfacePageHeader
            kicker="Assets"
            title="Assets"
            description={STANDFIRST}
            actions={
              <div className="flex items-center gap-2">
                <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                  {`${FLEET.length} machines · 3 divisions · 6 subclasses`}
                </span>
                <HelpAnchor doc="fleet/assets" />
              </div>
            }
          />

          <AskStrip view="fleet" />

          <Commentable name="AssetsBandTally">
            <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-muted">
              {bands.map((b) => (
                <li key={b.band} className="inline-flex items-center gap-1.5">
                  <span className={`band-dot band-${b.band}`} aria-hidden="true" />
                  <span>
                    {CONDITION_BAND_LABEL[b.band]} <span className="mono">{b.count}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Commentable>

          <Commentable name="AssetsDivisionCards">
            <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-3">
              {cards.map((card) => (
                <section
                  key={card.division}
                  className="flex flex-col gap-3 rounded-xl border p-4"
                  style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                >
                  <Link
                    to={card.to}
                    className="group flex items-start gap-2.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                      style={{ background: 'var(--surface-3)', color: 'var(--accent)' }}
                    >
                      <Box className="h-4 w-4" strokeWidth={1.7} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className="block text-[15px] font-semibold leading-tight tracking-tight"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {card.label}
                      </span>
                      <span className="mt-0.5 block text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                        {card.blurb}
                      </span>
                    </span>
                    <ChevronRight
                      className="mt-1 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
                      style={{ color: 'var(--ink-tertiary)' }}
                      aria-hidden="true"
                    />
                  </Link>

                  <p className="text-[11.5px] text-muted">
                    <b className="mono">{card.machines}</b> machines across{' '}
                    <b className="mono">{card.mines}</b> mines
                    {card.toWatch ? (
                      <>
                        {' · '}
                        <b className="mono" style={{ color: 'var(--band-vpoor)' }}>
                          {card.toWatch}
                        </b>{' '}
                        to watch
                      </>
                    ) : null}
                  </p>

                  <ul className="flex flex-col gap-1.5">
                    {card.subclasses.map((row) => (
                      <li key={row.subclass}>
                        <Link
                          to={row.to}
                          className="flex items-baseline justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                          style={{ borderColor: 'var(--hairline)' }}
                        >
                          <span className="text-[12.5px]" style={{ color: 'var(--ink-primary)' }}>
                            {row.subclass}
                          </span>
                          <span className="mono shrink-0 text-[11px] text-muted">
                            {row.machines}
                            {row.toWatch ? (
                              <b style={{ color: 'var(--band-vpoor)' }}>{` · ${row.toWatch}`}</b>
                            ) : null}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
